import { useCallback, useEffect, useState } from 'react';
import { AppFeatures, getAppFeatures } from '../services/appFeaturesService';

interface UseAppFeaturesReturn {
    features: AppFeatures | null;
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

/**
 * Loads platform feature flags. Consumers should treat a null value (still loading
 * or failed) as "show everything" so features are never hidden by accident while
 * flags are unavailable.
 */
export function useAppFeatures(): UseAppFeaturesReturn {
    const [features, setFeatures] = useState<AppFeatures | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetchFeatures = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getAppFeatures();
            setFeatures(data);
            setError(null);
        } catch (err) {
            setError(err as Error);
            setFeatures(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchFeatures();
    }, [fetchFeatures]);

    return { features, loading, error, refetch: fetchFeatures };
}
